import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, ExternalLink, FlaskConical, RotateCcw, Send } from "lucide-react";
import { SMB_INTAKE_FIXTURES } from "@shared/smb-intake-fixtures";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { z } from "zod";

const responseSchema = z.object({
  order: z.object({
    id: z.string(),
    externalId: z.string(),
    status: z.string(),
    isTest: z.boolean(),
  }),
  alreadyExisted: z.boolean(),
  reviewFlags: z.array(z.string()),
  httpStatus: z.union([z.literal(200), z.literal(202)]),
});

type ServerIssue = { path: string; message: string };
type SafeServerError = { message: string; issues: ServerIssue[] };

function safeServerError(error: unknown): SafeServerError {
  if (!(error instanceof Error)) return { message: "The intake could not be submitted. Try again.", issues: [] };
  const match = error.message.match(/^\d{3}:\s*([\s\S]*)$/);
  if (match) {
    try {
      const body = JSON.parse(match[1]) as { code?: unknown; message?: unknown; issues?: unknown };
      const message = typeof body.message === "string" && body.message.trim()
        ? body.message.trim().slice(0, 500)
        : "The server rejected this intake.";
      const issues = Array.isArray(body.issues) ? body.issues.slice(0, 30).flatMap((issue): ServerIssue[] => {
        if (!issue || typeof issue !== "object") return [];
        const value = issue as { path?: unknown; message?: unknown };
        if (typeof value.message !== "string") return [];
        const path = Array.isArray(value.path) ? value.path.map(String).join(".") : typeof value.path === "string" ? value.path : "";
        return [{ path: path.slice(0, 160), message: value.message.slice(0, 300) }];
      }) : [];
      return { message, issues };
    } catch {
      return { message: "The server rejected this intake. Review the JSON and try again.", issues: [] };
    }
  }
  return { message: "The intake could not be submitted. Try again.", issues: [] };
}

export function IntakeCard({ orderId, externalId, isTest, onSubmitted }: {
  orderId: string;
  externalId: string;
  isTest: boolean;
  onSubmitted?: () => void;
}) {
  const queryClient = useQueryClient();
  const [fixtureKey, setFixtureKey] = useState<string>(SMB_INTAKE_FIXTURES[0].key);
  const [json, setJson] = useState(() => JSON.stringify({ ...SMB_INTAKE_FIXTURES[0].input, externalId }, null, 2));
  const [localError, setLocalError] = useState("");
  const [serverError, setServerError] = useState<SafeServerError | null>(null);
  const [result, setResult] = useState<z.infer<typeof responseSchema> | null>(null);

  const submission = useMutation({
    mutationFn: async (body: unknown) => {
      const response = await apiRequest("POST", `/api/admin/smb/orders/${encodeURIComponent(orderId)}/submit-intake`, body);
      const parsed = responseSchema.safeParse(await response.json());
      if (!parsed.success) throw new Error("Invalid response");
      return parsed.data;
    },
    onSuccess: async (data) => {
      setServerError(null);
      setLocalError("");
      setResult(data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/admin/smb/orders", orderId] }),
        queryClient.invalidateQueries({ queryKey: ["/api/admin/smb/orders"] }),
      ]);
      onSubmitted?.();
    },
    onError: (error) => {
      setResult(null);
      setServerError(error instanceof Error && error.message === "Invalid response"
        ? { message: "The server response could not be read. Refresh the order before retrying.", issues: [] }
        : safeServerError(error));
    },
  });

  function chooseFixture(key: string) {
    const fixture = SMB_INTAKE_FIXTURES.find((item) => item.key === key);
    if (!fixture) return;
    setFixtureKey(key);
    setJson(JSON.stringify({ ...fixture.input, externalId }, null, 2));
    setLocalError("");
    setServerError(null);
    setResult(null);
  }

  function submit() {
    setLocalError("");
    setServerError(null);
    setResult(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      setLocalError("Enter valid JSON before submitting.");
      return;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      setLocalError("The intake must be a JSON object.");
      return;
    }
    submission.mutate({ ...parsed, externalId });
  }

  return <section aria-labelledby="smb-intake-heading" className="rounded-xl border bg-card p-4 sm:p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <h3 id="smb-intake-heading" className="text-sm font-semibold">Submit onboarding intake</h3>
          <Badge variant="outline" className="text-[9px]">Test fixture</Badge>
        </div>
        <p className="max-w-2xl text-[11px] leading-relaxed text-muted-foreground">
          Submit using your signed-in master account. No intake signing secret is needed. The selected order's external ID always replaces the JSON externalId.
        </p>
      </div>
      <div className="flex items-center gap-1.5 rounded-md border bg-card px-2 py-1.5 text-[10px] text-muted-foreground">
        <FlaskConical className="h-3.5 w-3.5 text-primary" />
        <span className="font-mono">{externalId}</span>
      </div>
    </div>

    <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(180px,240px)_1fr] sm:items-end">
      <label className="grid gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Fixture
        <Select value={fixtureKey} onValueChange={chooseFixture} disabled={submission.isPending}>
          <SelectTrigger aria-label="Choose intake fixture" className="h-9 bg-card text-xs font-normal normal-case tracking-normal">
            <SelectValue placeholder="Choose a fixture" />
          </SelectTrigger>
          <SelectContent>
            {SMB_INTAKE_FIXTURES.map((fixture) => <SelectItem key={fixture.key} value={fixture.key}>{fixture.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </label>
      <p className="text-[10px] leading-relaxed text-muted-foreground">
        Fixtures cover a full local-service profile, a no-logo review case, and an Instagram-only profile. Server validation is authoritative.
      </p>
    </div>

    <label htmlFor="smb-intake-json" className="mt-4 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Editable intake JSON</label>
    <textarea
      id="smb-intake-json"
      value={json}
      onChange={(event) => { setJson(event.target.value); setLocalError(""); setServerError(null); setResult(null); }}
      disabled={submission.isPending}
      spellCheck={false}
      aria-describedby="smb-intake-json-hint"
      className="mt-1.5 min-h-72 w-full resize-y rounded-lg border border-border bg-card px-3 py-3 font-mono text-[11px] leading-relaxed text-foreground outline-none transition focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
    />
    <p id="smb-intake-json-hint" className="mt-1 text-[10px] text-muted-foreground">The externalId is pinned to the current order at submission time.</p>

    {!isTest && <div className="mt-3 flex items-start gap-2 rounded-lg border bg-muted/50 p-3 text-[11px] leading-relaxed text-foreground">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>Intake submission is disabled for live orders. Use a test order to exercise this flow.</span>
    </div>}
    {localError && <p role="alert" className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{localError}</p>}
    {serverError && <div role="alert" className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs text-destructive">
      <p className="font-medium">{serverError.message}</p>
      {serverError.issues.length > 0 && <ul className="mt-2 space-y-1">
        {serverError.issues.map((issue, index) => <li key={`${issue.path}-${index}`} className="break-words">{issue.path && <span className="font-mono">{issue.path}: </span>}{issue.message}</li>)}
      </ul>}
    </div>}
    {result && <div role="status" className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-foreground">
        <Check className="h-4 w-4" />
        {result.alreadyExisted ? "Replay accepted, existing intake returned" : "Intake accepted"}
        <Badge variant="outline" className="text-[9px]">{result.httpStatus}</Badge>
        <Badge variant="outline" className="text-[9px]">{result.order.status.replaceAll("_", " ")}</Badge>
      </div>
      <p className="mt-1 font-mono text-[10px] text-muted-foreground">{result.order.externalId} · {result.order.id}</p>
      {result.reviewFlags.length > 0 && <div className="mt-2 rounded-md border bg-muted/50 px-2.5 py-2 text-[10px] text-foreground">
        <p className="font-semibold">Review hold</p>
        <ul className="mt-1 list-inside list-disc">{result.reviewFlags.map((flag) => <li key={flag} className="break-words">{flag.replaceAll("_", " ")}</li>)}</ul>
      </div>}
      <a className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-semibold text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" href={`/admin/smb/orders/${encodeURIComponent(result.order.id)}`}>
        Open returned order <ExternalLink className="h-3 w-3" />
      </a>
    </div>}

    <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-3">
      <Button size="sm" onClick={submit} disabled={!isTest || submission.isPending}>
        {submission.isPending ? <><RotateCcw className="mr-1.5 h-3.5 w-3.5 animate-spin" />Submitting</> : <><Send className="mr-1.5 h-3.5 w-3.5" />Submit intake</>}
      </Button>
      <span aria-live="polite" className="text-[10px] text-muted-foreground">{submission.isPending ? "Submitting securely…" : result ? "Order details refreshed" : ""}</span>
    </div>
  </section>;
}
